import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, ExternalLink, Clock, CheckCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function CustomerApprovalsPage() {
  const [searchParams] = useSearchParams();
  const customerEmail = searchParams.get("email");
  
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState({});
  const [submitting, setSubmitting] = useState({});

  const fetchApprovals = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/proof/customer-approvals?email=${encodeURIComponent(customerEmail)}`);
      setApprovals(response.data.approvals);
      
      // Initialize comments object
      const initialComments = {};
      response.data.approvals.forEach(approval => {
        if (approval.status === 'pending') {
          initialComments[approval.id] = '';
        }
      });
      setComments(initialComments);
    } catch (error) {
      toast.error("Failed to load approvals");
    } finally {
      setLoading(false);
    }
  }, [customerEmail]);

  useEffect(() => {
    if (customerEmail) {
      fetchApprovals();
    }
  }, [customerEmail, fetchApprovals]);

  const handleResponse = async (approvalId, status) => {
    const comment = comments[approvalId]?.trim();
    
    if (!comment) {
      toast.error('Please add a comment before submitting');
      return;
    }

    if (!window.confirm(`Are you sure you want to ${status} this proof?`)) return;

    setSubmitting(prev => ({ ...prev, [approvalId]: true }));
    
    try {
      await axios.put(`${API}/api/proof/proof-approvals/${approvalId}/respond`, {
        status,
        customer_comment: comment
      });
      toast.success(`Proof ${status} successfully!`);
      await fetchApprovals(); // Refresh list
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${status} proof`);
    } finally {
      setSubmitting(prev => ({ ...prev, [approvalId]: false }));
    }
  };

  if (!customerEmail) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">Invalid Link</h1>
          <p className="text-gray-600 mt-2">This approval link is invalid or expired.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
      </div>
    );
  }

  const pendingApprovals = approvals.filter(a => a.status === 'pending');
  const completedApprovals = approvals.filter(a => a.status !== 'pending');

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-8 gap-3">
          <Logo size={48} showWordmark={false} />
          <h1 className="text-4xl font-bold text-gray-900 mb-1">Your Proof Approvals</h1>
          <p className="text-gray-600">analyticaY Print Shop</p>
          <p className="text-sm text-gray-500">{customerEmail}</p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <p className="text-3xl font-bold text-gray-900">{approvals.length}</p>
            <p className="text-sm text-gray-600">Total Approvals</p>
          </div>
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg shadow p-4 text-center">
            <p className="text-3xl font-bold text-yellow-600">{pendingApprovals.length}</p>
            <p className="text-sm text-yellow-700">Pending</p>
          </div>
          <div className="bg-green-50 border-2 border-green-200 rounded-lg shadow p-4 text-center">
            <p className="text-3xl font-bold text-green-600">{completedApprovals.length}</p>
            <p className="text-sm text-green-700">Completed</p>
          </div>
        </div>

        {/* Pending Approvals */}
        {pendingApprovals.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Clock className="h-6 w-6 mr-2 text-yellow-600" />
              Pending Approvals ({pendingApprovals.length})
            </h2>
            <div className="space-y-4">
              {pendingApprovals.map((approval) => (
                <div key={approval.id} className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-yellow-500">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">{approval.artwork_name}</h3>
                      <p className="text-sm text-gray-600">Event: {approval.event_code}</p>
                    </div>
                    <Badge className="bg-yellow-500 text-white">
                      <Clock className="h-3 w-3 mr-1" />
                      Pending
                    </Badge>
                  </div>

                  {approval.notes && (
                    <div className="mb-4 p-3 bg-blue-50 rounded border border-blue-200">
                      <p className="text-sm text-blue-900"><strong>Notes:</strong> {approval.notes}</p>
                    </div>
                  )}

                  <a
                    href={approval.google_drive_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded mb-4 transition-colors inline-block"
                  >
                    <ExternalLink className="h-4 w-4" />
                    View Proof in Google Drive
                  </a>

                  <div className="mb-4">
                    <label className="block font-semibold text-gray-900 mb-2">
                      Your Comment (Required) *
                    </label>
                    <Textarea
                      value={comments[approval.id] || ''}
                      onChange={(e) => setComments({ ...comments, [approval.id]: e.target.value })}
                      placeholder="Please add your feedback about this proof..."
                      className="w-full min-h-[80px] border-2"
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button
                      onClick={() => handleResponse(approval.id, 'approved')}
                      disabled={submitting[approval.id]}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 text-base font-semibold"
                    >
                      {submitting[approval.id] ? (
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      ) : (
                        <CheckCircle2 className="h-5 w-5 mr-2" />
                      )}
                      Approve
                    </Button>
                    <Button
                      onClick={() => handleResponse(approval.id, 'rejected')}
                      disabled={submitting[approval.id]}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 text-base font-semibold"
                    >
                      {submitting[approval.id] ? (
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      ) : (
                        <XCircle className="h-5 w-5 mr-2" />
                      )}
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Completed Approvals */}
        {completedApprovals.length > 0 && (
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <CheckCircle className="h-6 w-6 mr-2 text-green-600" />
              Completed Approvals ({completedApprovals.length})
            </h2>
            <div className="space-y-3">
              {completedApprovals.map((approval) => (
                <div key={approval.id} className={`bg-white rounded-lg shadow p-4 border-l-4 ${approval.status === 'approved' ? 'border-green-500' : 'border-red-500'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-900">{approval.artwork_name}</h4>
                      <p className="text-sm text-gray-600">Event: {approval.event_code}</p>
                      {approval.customer_comment && (
                        <p className="text-sm text-gray-700 mt-1">Comment: {approval.customer_comment}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <Badge className={approval.status === 'approved' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}>
                        {approval.status === 'approved' ? (
                          <><CheckCircle className="h-3 w-3 mr-1" />Approved</>
                        ) : (
                          <><XCircle className="h-3 w-3 mr-1" />Rejected</>
                        )}
                      </Badge>
                      {approval.submitted_at && (
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(approval.submitted_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {approvals.length === 0 && (
          <div className="bg-white rounded-lg shadow-lg p-12 text-center">
            <CheckCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Approvals Found</h3>
            <p className="text-gray-600">You don't have any proof approvals at this time.</p>
          </div>
        )}
      </div>
    </div>
  );
}
